import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import type { IInfluencer } from '../../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BUZZ_BRIDGE_PROFILE_FIELD_LABELS,
    getBuzzBridgeProfileMissingFields,
} from '../utils/buzzBridgeProfileCompleteness';

const PROFILE_FIELD_COUNT = 7;
const SURFACE = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';
const ERROR = '#ff7351';

function sessionDismissKey(influencerId: string): string {
    return `bb_profile_incomplete_banner_dismissed_${influencerId}`;
}

export interface BuzzBridgeProfileIncompleteBannerProps {
    influencer: IInfluencer;
    onCompleteProfile: () => void;
}

export function BuzzBridgeProfileIncompleteBanner({
    influencer,
    onCompleteProfile,
}: BuzzBridgeProfileIncompleteBannerProps) {
    const missing = useMemo(() => getBuzzBridgeProfileMissingFields(influencer), [influencer]);
    const influencerId = influencer._id ?? '';
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!influencerId || typeof window === 'undefined') return;
        try {
            setDismissed(sessionStorage.getItem(sessionDismissKey(influencerId)) === '1');
        } catch {
            setDismissed(false);
        }
    }, [influencerId, missing.length]);

    const handleDismiss = useCallback(() => {
        if (!influencerId) return;
        try {
            sessionStorage.setItem(sessionDismissKey(influencerId), '1');
        } catch {
            /** quota / private mode */
        }
        setDismissed(true);
    }, [influencerId]);

    if (!influencerId || missing.length === 0 || dismissed) return null;

    const pct = Math.round(((PROFILE_FIELD_COUNT - missing.length) / PROFILE_FIELD_COUNT) * 100);
    const chips = missing.slice(0, 3);

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: SURFACE,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0,253,193,0.1)',
                        color: MINT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <VerifiedUserOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: '#fff' }}>
                            Profile Readiness: {pct}%
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 10, color: ON_VARIANT }}>
                            ({missing.length} {missing.length === 1 ? 'step' : 'steps'} to 100%)
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: { xs: '100%', sm: 320 },
                            maxWidth: '100%',
                            height: 6,
                            borderRadius: 999,
                            bgcolor: SURFACE_HI,
                            mt: 0.75,
                            overflow: 'hidden',
                        }}>
                        <Box
                            sx={{
                                width: `${pct}%`,
                                height: '100%',
                                borderRadius: 999,
                                bgcolor: MINT,
                                boxShadow: '0 0 8px #00fdc1',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {chips.map(field => (
                    <Box
                        key={field}
                        sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: SURFACE_HI,
                            color: ERROR,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 11,
                        }}>
                        <PriorityHighIcon sx={{ fontSize: 14 }} />
                        {BUZZ_BRIDGE_PROFILE_FIELD_LABELS[field]}
                    </Box>
                ))}
                <Button
                    onClick={onCompleteProfile}
                    sx={{
                        px: 1.75,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: GOLD,
                        color: '#392700',
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        '&:hover': { bgcolor: '#ecaa00' },
                    }}>
                    Complete Profile
                </Button>
                <IconButton
                    aria-label="Dismiss profile reminder"
                    onClick={handleDismiss}
                    size="small"
                    sx={{ color: ON_VARIANT }}>
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}
